import React, { useState, useRef, useCallback, useEffect, useMemo } from "react";
import { getApiBaseUrl } from "../../../../../helpers/apiUrl";
import MathContent from "../../../../customComponents/MathContent";
import ReviewStep from "../shared/ReviewStep";
import QuizPlayer from "../shared/QuizPlayer";
import TopicPicker from "../shared/TopicPicker";
import {
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Button,
  Grid,
  LinearProgress,
  TextField,
  Chip,
  Alert,
  Skeleton,
  Dialog,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import {
  ArrowBack,
  SmartToy,
  CheckCircle,
  EmojiEvents,
  AutoAwesome,
  Bolt,
  Add,
  TrendingUp,
  StarOutline,
} from "@mui/icons-material";
import { keyframes } from "@emotion/react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useGetQuizResultQuery, useGetMyStudentSubjectsQuery, useGetMySubjectLevelsQuery, useInitSubjectLevelMutation, useGetQuizByIdQuery } from "../../../../../app/quizApiSlice";
import { useGetLearnHistoryQuery } from "../../../../../app/learnApiSlice";
import { saveActiveQuizSession, getActiveQuizSession, clearActiveQuizSession } from "../../../../../helpers/activeQuizSession";

// ─── Game-themed keyframes ──────────────────────────────────────────────────
const popIn = keyframes`
  from { opacity: 0; transform: scale(0.85) translateY(12px); }
  to   { opacity: 1; transform: scale(1)    translateY(0); }
`;
const confettiFall = keyframes`
  0%   { transform: translateY(-20px) rotate(0deg);   opacity: 1; }
  100% { transform: translateY(120px) rotate(540deg); opacity: 0; }
`;
const scorePop = keyframes`
  0%   { transform: scale(0.5); opacity: 0; }
  60%  { transform: scale(1.2); opacity: 1; }
  100% { transform: scale(1);   opacity: 1; }
`;
const correctFlash = keyframes`
  0%   { background-color: inherit; }
  30%  { background-color: rgba(22, 163, 74, 0.35); }
  100% { background-color: inherit; }
`;
const wrongFlash = keyframes`
  0%   { background-color: inherit; }
  30%  { background-color: rgba(220, 38, 38, 0.3); }
  100% { background-color: inherit; }
`;
const typingBounce = keyframes`
  0%, 60%, 100% { transform: translateY(0);   opacity: 0.45; }
  30%           { transform: translateY(-6px); opacity: 1; }
`;



// ─── Step IDs ────────────────────────────────────────────────────────────────
const STEP_CHAT = "chat";
const STEP_QUIZ = "quiz";
const STEP_RESULT = "result";
const STEP_REVIEW = "review";

// ─── Fullscreen dark background wrapper ──────────────────────────────────────
const PracticeBg = ({ children }) => (
  <Box
    sx={{
      background: "linear-gradient(160deg, #071524 0%, #0d2137 50%, #071524 100%)",
      minHeight: "100vh",
      p: { xs: 2.5, sm: 3 },
      pr: { xs: 2.5, sm: 9 },
    }}
  >
    {children}
  </Box>
);

const coachSlideUp = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const ResultStep = ({ quizId, onReview, onNewQuiz }) => {
  const { data, isLoading } = useGetQuizResultQuery({ quiz_id: quizId });

  const stats = React.useMemo(() => {
    if (!data?.answers) return { correct: 0, wrong: 0, total: 0, pct: 0 };
    let correct = 0;
    data.answers.forEach((a) => {
      if (a.question?.options?.find((o) => o.id === a.option_id && o.is_answer)) correct++;
    });
    const total = data.answers.length;
    return { correct, wrong: total - correct, total, pct: total ? Math.round((correct / total) * 100) : 0 };
  }, [data]);

  if (isLoading) return <Box sx={{ textAlign: "center", py: 6 }}><CircularProgress sx={{ color: "#fff" }} /></Box>;

  const stars = stats.pct >= 80 ? 3 : stats.pct >= 55 ? 2 : 1;
  const emoji = stats.pct >= 80 ? "🏆" : stats.pct >= 55 ? "🎯" : "💪";

  const confettiDots = Array.from({ length: 16 }, (_, i) => ({
    left: `${Math.random() * 90 + 5}%`,
    color: ["#FF6B9D", "#FFD700", "#4FC3F7", "#A5D6A7", "#FF8A65", "#CE93D8"][i % 6],
    delay: `${(Math.random() * 0.4).toFixed(2)}s`,
    size: Math.floor(Math.random() * 8 + 6),
  }));

  return (
    <Box
      sx={{
        background: "linear-gradient(160deg, #1a0533 0%, #2d1b69 50%, #1a0533 100%)",
        p: { xs: 3, sm: 4 },
        minHeight: "100vh",
        textAlign: "center",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {confettiDots.map((d, i) => (
        <Box key={i} sx={{ position: "absolute", top: "-10px", left: d.left, width: d.size, height: d.size, borderRadius: "50%", bgcolor: d.color, animation: `${confettiFall} 1.2s ${d.delay} ease-out forwards` }} />
      ))}

      <Typography sx={{ fontSize: "4rem", lineHeight: 1, mb: 1 }}>{emoji}</Typography>
      <Box sx={{ display: "flex", justifyContent: "center", gap: 1, mb: 2 }}>
        {[1, 2, 3].map((s) => (
          <Typography key={s} sx={{ fontSize: "2rem", filter: s <= stars ? "drop-shadow(0 0 6px #FFD700)" : "grayscale(1) opacity(0.3)", animation: s <= stars ? `${scorePop} 0.5s ${(s - 1) * 0.15}s ease both` : "none" }}>⭐</Typography>
        ))}
      </Box>
      <Box sx={{ display: "inline-block", px: 4, py: 1.5, mb: 2, borderRadius: "50px", background: "rgba(255,255,255,0.12)", border: "2px solid rgba(255,255,255,0.25)", animation: `${scorePop} 0.6s 0.1s ease both` }}>
        <Typography sx={{ fontWeight: 900, fontSize: "2.8rem", color: "#fff", lineHeight: 1 }}>
          {stats.pct}<Typography component="span" sx={{ fontSize: "1.2rem", color: "rgba(255,255,255,0.7)" }}>%</Typography>
        </Typography>
      </Box>
      <Typography sx={{ color: "rgba(255,255,255,0.7)", mb: 3 }}>{stats.correct} correct out of {stats.total} questions</Typography>
      <Grid container spacing={2} justifyContent="center" sx={{ mb: 4, maxWidth: 320, mx: "auto" }}>
        <Grid item xs={6}>
          <Box sx={{ p: 2, borderRadius: "14px", background: "rgba(34,197,94,0.18)", border: "1.5px solid rgba(34,197,94,0.4)" }}>
            <Typography sx={{ fontWeight: 900, fontSize: "1.6rem", color: "#4ade80" }}>{stats.correct}</Typography>
            <Typography sx={{ fontSize: "0.75rem", color: "rgba(255,255,255,0.6)", fontWeight: 600 }}>CORRECT</Typography>
          </Box>
        </Grid>
        <Grid item xs={6}>
          <Box sx={{ p: 2, borderRadius: "14px", background: "rgba(239,68,68,0.18)", border: "1.5px solid rgba(239,68,68,0.4)" }}>
            <Typography sx={{ fontWeight: 900, fontSize: "1.6rem", color: "#f87171" }}>{stats.wrong}</Typography>
            <Typography sx={{ fontSize: "0.75rem", color: "rgba(255,255,255,0.6)", fontWeight: 600 }}>WRONG</Typography>
          </Box>
        </Grid>
      </Grid>

      {/* CTA buttons */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, maxWidth: 320, mx: "auto" }}>
        {stats.wrong > 0 && (
          <Button
            onClick={onReview}
            variant="contained"
            startIcon={<AutoAwesome />}
            sx={{
              background: "linear-gradient(135deg, #059669, #34d399)",
              color: "#fff", fontWeight: 800, borderRadius: "14px", py: 1.4, fontSize: "0.95rem",
              boxShadow: "0 6px 20px rgba(5,150,105,0.4)",
              "&:hover": { background: "linear-gradient(135deg, #047857, #10b981)", boxShadow: "0 8px 24px rgba(5,150,105,0.5)" },
            }}
          >
            Review Wrong Answers
          </Button>
        )}
        <Button
          variant="outlined"
          onClick={onNewQuiz}
          sx={{ color: "rgba(255,255,255,0.85)", borderColor: "rgba(255,255,255,0.3)", fontWeight: 700, borderRadius: "14px", py: 1.2, "&:hover": { borderColor: "rgba(255,255,255,0.7)", bgcolor: "rgba(255,255,255,0.08)" } }}
        >
          New Quiz
        </Button>
      </Box>
    </Box>
  );
};
// ─── Quick subject suggestions ────────────────────────────────────────────────
const FALLBACK_SUBJECTS = ["Mathematics", "Physics", "Chemistry", "Biology", "English Language", "History", "Geography", "Computer Science", "Economics", "Further Maths"];

// Subject colour palette (cycles through)
const SUBJECT_PALETTES = [
  { bg: "#1e3a5f", accent: "#3b82f6", glow: "rgba(59,130,246,0.35)" },
  { bg: "#1a3828", accent: "#22c55e", glow: "rgba(34,197,94,0.35)" },
  { bg: "#3b1a4a", accent: "#a855f7", glow: "rgba(168,85,247,0.35)" },
  { bg: "#3d1f06", accent: "#f97316", glow: "rgba(249,115,22,0.35)" },
  { bg: "#1e2a4a", accent: "#6366f1", glow: "rgba(99,102,241,0.35)" },
  { bg: "#1a3030", accent: "#14b8a6", glow: "rgba(20,184,166,0.35)" },
  { bg: "#3d1a1a", accent: "#ef4444", glow: "rgba(239,68,68,0.35)" },
  { bg: "#2a2a0e", accent: "#eab308", glow: "rgba(234,179,8,0.35)" },
];

// ─── Subject Card ──────────────────────────────────────────────────────────────
const SubjectCard = ({ name, palette, onClick, delay = "0s", isGenerating, personalLevel }) => {
  const [hovered, setHovered] = useState(false);
  const initials = name.split(" ").slice(0, 2).map((w) => w[0]?.toUpperCase() || "").join("");
  return (
    <Box
      onClick={isGenerating ? undefined : onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{
        background: hovered ? `${palette.bg}ee` : palette.bg,
        borderRadius: "20px",
        p: "20px 18px",
        cursor: isGenerating ? "default" : "pointer",
        position: "relative",
        overflow: "hidden",
        transition: "all 0.2s ease",
        transform: hovered && !isGenerating ? "translateY(-4px) scale(1.02)" : "scale(1)",
        boxShadow: hovered && !isGenerating
          ? `0 12px 32px ${palette.glow}, 0 2px 8px rgba(0,0,0,0.4)`
          : "0 4px 16px rgba(0,0,0,0.35)",
        border: `1.5px solid ${hovered && !isGenerating ? palette.accent : "rgba(255,255,255,0.08)"}`,
        animation: `${popIn} 0.4s ${delay} ease both`,
        opacity: isGenerating ? 0.55 : 1,
        userSelect: "none",
      }}
    >
      <Box sx={{ position: "absolute", top: -16, right: -16, width: 80, height: 80, borderRadius: "50%", bgcolor: `${palette.accent}18`, pointerEvents: "none" }} />
      <Box sx={{
        width: 44, height: 44, borderRadius: "14px",
        bgcolor: `${palette.accent}22`, border: `1.5px solid ${palette.accent}55`,
        display: "flex", alignItems: "center", justifyContent: "center",
        mb: 1.5,
      }}>
        <Typography sx={{ fontWeight: 900, fontSize: "1rem", color: palette.accent }}>{initials}</Typography>
      </Box>
      <Typography sx={{ fontWeight: 800, fontSize: "0.95rem", color: "#fff", lineHeight: 1.3, mb: 0.3 }}>
        {name}
      </Typography>
      {personalLevel ? (
        <LevelBadge level={personalLevel} />
      ) : (
        <Typography sx={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.4)", fontWeight: 500 }}>Tap to start</Typography>
      )}
    </Box>
  );
};

// ─── Level picker tiers available for first-game selection ──────────────────
const STARTER_TIERS = [
  { value: "Starter",      emoji: "🌱", desc: "Brand new to this subject" },
  { value: "Beginner",     emoji: "📚", desc: "Know a few basics" },
  { value: "Intermediate", emoji: "🎯", desc: "Comfortable with the core topics" },
];

// ─── Subject level badge (shown on each subject card) ────────────────────────
const LevelBadge = ({ level }) => {
  if (!level) return null;
  const color = level === "Expert" ? "#FFD700" : level === "Advanced" ? "#a855f7" : level === "Intermediate" ? "#22c55e" : level === "Beginner" ? "#42A5F5" : "rgba(255,255,255,0.35)";
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.4, mt: 0.5 }}>
      <TrendingUp sx={{ fontSize: 11, color }} />
      <Typography sx={{ fontSize: "0.65rem", fontWeight: 700, color, letterSpacing: 0.5 }}>{level}</Typography>
    </Box>
  );
};

// ─── Level-up celebration modal ───────────────────────────────────────────────
const LevelUpModal = ({ open, onClose, subject, newLevel, xpEarned }) => (
  <Dialog
    open={open}
    onClose={onClose}
    sx={{ zIndex: 1500 }}
    PaperProps={{ sx: { bgcolor: "#0d2137", border: "1.5px solid #FFD700", borderRadius: "24px", textAlign: "center", p: 3, maxWidth: 320, mx: "auto" } }}
  >
    <DialogTitle sx={{ p: 0, mb: 1 }}>
      <Typography sx={{ fontSize: "3rem", lineHeight: 1 }}>🎉</Typography>
    </DialogTitle>
    <DialogContent sx={{ p: 0 }}>
      <Typography sx={{ fontWeight: 900, fontSize: "1.3rem", color: "#FFD700", mb: 0.5 }}>Level Up!</Typography>
      <Typography sx={{ color: "rgba(255,255,255,0.7)", fontSize: "0.9rem", mb: 1.5 }}>
        You reached <strong style={{ color: "#fff" }}>{newLevel}</strong> in {subject}!
      </Typography>
      {xpEarned > 0 && (
        <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, px: 2, py: 0.5, borderRadius: "50px", bgcolor: "rgba(255,215,0,0.12)", border: "1px solid rgba(255,215,0,0.3)", mb: 2 }}>
          <StarOutline sx={{ fontSize: 14, color: "#FFD700" }} />
          <Typography sx={{ fontWeight: 800, fontSize: "0.85rem", color: "#FFD700" }}>+{xpEarned} XP</Typography>
        </Box>
      )}
      <Button
        onClick={onClose}
        variant="contained"
        fullWidth
        sx={{ background: "linear-gradient(90deg, #b7791f, #FFD700)", color: "#000", fontWeight: 800, borderRadius: "12px" }}
      >
        Keep Going! 🚀
      </Button>
    </DialogContent>
  </Dialog>
);

// ─── First-game level picker dialog ─────────────────────────────────────────
const LevelPickerDialog = ({ open, subject, onSelect, onClose }) => (
  <Dialog
    open={open}
    onClose={onClose}
    sx={{ zIndex: 1500 }}
    PaperProps={{ sx: { bgcolor: "#0d2137", border: "1.5px solid rgba(66,165,245,0.4)", borderRadius: "24px", p: 3, maxWidth: 360, mx: "auto" } }}
  >
    <DialogTitle sx={{ p: 0, mb: 2 }}>
      <Typography sx={{ fontWeight: 900, fontSize: "1.1rem", color: "#fff", textAlign: "center" }}>
        How familiar are you with {subject}?
      </Typography>
      <Typography sx={{ fontSize: "0.78rem", color: "rgba(255,255,255,0.4)", textAlign: "center", mt: 0.5 }}>
        This sets your starting game level — it changes as you improve
      </Typography>
    </DialogTitle>
    <DialogContent sx={{ p: 0, display: "flex", flexDirection: "column", gap: 1 }}>
      {STARTER_TIERS.map((tier) => (
        <Box
          key={tier.value}
          onClick={() => onSelect(tier.value)}
          sx={{
            display: "flex", alignItems: "center", gap: 1.5, p: "14px 18px",
            borderRadius: "14px", cursor: "pointer",
            border: "1.5px solid rgba(66,165,245,0.25)",
            bgcolor: "rgba(255,255,255,0.04)",
            "&:hover": { bgcolor: "rgba(66,165,245,0.1)", borderColor: "rgba(66,165,245,0.6)" },
            transition: "all 0.15s ease",
          }}
        >
          <Typography sx={{ fontSize: "1.4rem" }}>{tier.emoji}</Typography>
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: "0.95rem", color: "#fff" }}>{tier.value}</Typography>
            <Typography sx={{ fontSize: "0.72rem", color: "rgba(255,255,255,0.45)" }}>{tier.desc}</Typography>
          </Box>
        </Box>
      ))}
    </DialogContent>
  </Dialog>
);

// ─── Main Component ───────────────────────────────────────────────────────────
const PracticeQuizFlow = () => {
  const [step, setStep] = useState(STEP_CHAT);
  const [subject, setSubject] = useState("");
  const [customSubject, setCustomSubject] = useState("");
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [topicPickerSubject, setTopicPickerSubject] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatingStatus, setGeneratingStatus] = useState("");
  const [error, setError] = useState("");
  const [quiz, setQuiz] = useState(null);
  const [completedQuizId, setCompletedQuizId] = useState(null);
  const [showReview, setShowReview] = useState(false);
  const [levelUpData, setLevelUpData] = useState(null);    // {subject, level, xp_earned}
  // quizId being restored from localStorage (null = no restore in progress)
  const [restoredQuizId, setRestoredQuizId] = useState(null);

  const authData = useSelector((state) => state.auth);
  const accessToken = authData?.access_token;
  const userId = authData?.user?.id;
  const baseUrl = getApiBaseUrl();
  const navigate = useNavigate();

  const { data: subjectsData, isLoading: subjectsLoading } = useGetMyStudentSubjectsQuery();
  const { data: levelsData, refetch: refetchLevels } = useGetMySubjectLevelsQuery();
  const [initSubjectLevel] = useInitSubjectLevelMutation();
  // Fetches the quiz when restoring a session from localStorage on refresh
  const { data: restoredQuizData } = useGetQuizByIdQuery(restoredQuizId, { skip: !restoredQuizId });

  // On mount: check localStorage for an interrupted quiz session and restore it
  useEffect(() => {
    if (!userId) return;
    const session = getActiveQuizSession(userId);
    if (session?.quizId) {
      setRestoredQuizId(session.quizId);
      setSubject(session.subject || "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // When the restored quiz data arrives, jump straight to the quiz step
  useEffect(() => {
    if (!restoredQuizData?.quiz) return;
    setQuiz(restoredQuizData.quiz);
    setStep(STEP_QUIZ);
    setRestoredQuizId(null);
  }, [restoredQuizData]);

  // Fetch recent learn history for "Continue Last Lesson" option
  const { data: historyData } = useGetLearnHistoryQuery({ page: 1, per_page: 10 });
  const lastTopicsForSubject = useMemo(() => {
    if (!topicPickerSubject || !historyData?.sessions) return [];
    const match = historyData.sessions.find(
      (s) => s.subject?.toLowerCase() === topicPickerSubject.toLowerCase() && s.topics?.length
    );
    return match?.topics || [];
  }, [topicPickerSubject, historyData]);

  const subjects = subjectsData?.subjects?.length
    ? subjectsData.subjects.map((s) => s.name)
    : FALLBACK_SUBJECTS;

  // Map subject (lowercase) → {level, xp, xp_to_next}
  const subjectLevelsMap = React.useMemo(() => {
    const map = {};
    (levelsData?.subject_levels || []).forEach((r) => { map[r.subject.toLowerCase()] = r; });
    return map;
  }, [levelsData]);

  const generateQuiz = useCallback(async (subjectName, personalLevel, focusTopic, { continuation = false } = {}) => {
    if (!subjectName?.trim() || isGenerating) return;
    setError("");
    setQuiz(null);
    setSubject(subjectName);
    setIsGenerating(true);
    setGeneratingStatus(continuation ? "📈 Building follow-up quiz…" : "✨ Building your personalised quiz…");

    try {
      const response = await fetch(`${baseUrl}/quiz/student/ai/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify({
          subject: subjectName.trim(),
          level: personalLevel || undefined,
          focus_topic: focusTopic || null,
          num_questions: null,
          adaptive: true,
          continuation: continuation || undefined,
        }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || `Request failed (${response.status})`);
      setQuiz(data.quiz);
      setStep(STEP_QUIZ);
      // Persist session so the quiz survives a page refresh
      if (userId) saveActiveQuizSession(userId, { quizId: data.quiz.id, subject: subjectName });
    } catch (err) {
      setError(err.message || "Failed to generate quiz. Please try again.");
      setStep(STEP_CHAT);
    } finally {
      setIsGenerating(false);
      setGeneratingStatus("");
    }
  }, [isGenerating, baseUrl, accessToken]);

  // Called when a subject card is tapped — opens topic picker.
  const handleSubjectSelect = useCallback((subjectName) => {
    setTopicPickerSubject(subjectName);
  }, []);

  // Called when topic is chosen from the picker.
  const handleTopicChosen = useCallback(async (topic, opts = {}) => {
    const subjectName = topicPickerSubject;
    setTopicPickerSubject(null);
    if (!subjectName) return;

    const key = subjectName.toLowerCase();
    const existing = subjectLevelsMap[key];
    const level = existing?.level || "Starter";
    if (!existing) {
      try {
        await initSubjectLevel({ subject: subjectName, level }).unwrap();
        refetchLevels();
      } catch (_) { /* non-critical */ }
    }
    generateQuiz(subjectName, level, topic, opts);
  }, [topicPickerSubject, subjectLevelsMap, generateQuiz, initSubjectLevel, refetchLevels]);

  const handleQuizDoneWithLevel = useCallback((qid, subjectLevelInfo) => {
    if (userId) clearActiveQuizSession(userId);
    setCompletedQuizId(qid);
    if (subjectLevelInfo?.leveled_up) {
      setLevelUpData(subjectLevelInfo);
    }
    refetchLevels();
    setStep(STEP_RESULT);
  }, [refetchLevels, userId]);

  const handleQuizDone = (qid) => { if (userId) clearActiveQuizSession(userId); setCompletedQuizId(qid); setStep(STEP_RESULT); };
  const handleNewQuiz = () => { if (userId) clearActiveQuizSession(userId); setStep(STEP_CHAT); setQuiz(null); setSubject(""); setCustomSubject(""); setShowCustomInput(false); setTopicPickerSubject(null); setError(""); setShowReview(false); };
  const handleReview = () => setShowReview(true);

  if (step === STEP_CHAT) {
    // ── Restore loading overlay (quiz being fetched from server on refresh) ───
    if (restoredQuizId) {
      return (
        <Box sx={{ background: "linear-gradient(160deg, #071524 0%, #0d2137 50%, #071524 100%)", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 2 }}>
          <CircularProgress sx={{ color: "#42A5F5" }} />
          <Typography sx={{ color: "rgba(255,255,255,0.7)", fontWeight: 600 }}>Resuming your quiz…</Typography>
        </Box>
      );
    }
    if (isGenerating) {
      return (
        <Box sx={{ background: "linear-gradient(160deg, #071524 0%, #0d2137 50%, #071524 100%)", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", px: 3 }}>
          <Box sx={{ position: "relative", display: "inline-flex", mb: 3 }}>
            <CircularProgress size={80} thickness={2} sx={{ color: "rgba(66,165,245,0.15)" }} variant="determinate" value={100} />
            <CircularProgress size={80} thickness={2.5} sx={{ color: "#42A5F5", position: "absolute", top: 0, left: 0, animationDuration: "1.8s" }} />
            <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <AutoAwesome sx={{ fontSize: 32, color: "#42A5F5" }} />
            </Box>
          </Box>
          <Typography sx={{ fontWeight: 800, fontSize: "1.2rem", color: "#fff", mb: 0.75, textAlign: "center" }}>
            Building your {subject} quiz…
          </Typography>
          <Typography sx={{ fontSize: "0.85rem", color: "rgba(255,255,255,0.4)", textAlign: "center", mb: 3 }}>
            Adapting to your performance history
          </Typography>
          <Box sx={{ display: "flex", gap: 0.8 }}>
            {[0, 1, 2, 3].map((d) => (
              <Box key={d} sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "#42A5F5", opacity: 0.5 + d * 0.15, animation: `${typingBounce} 1.4s ${d * 0.18}s ease-in-out infinite` }} />
            ))}
          </Box>
        </Box>
      );
    }

    // ── Subject picker ─────────────────────────────────────────────────────
    return (
      <Box sx={{ background: "linear-gradient(160deg, #071524 0%, #0d2137 50%, #071524 100%)", minHeight: "100vh", px: { xs: 2, sm: 3 }, pr: { xs: 2, sm: 9 } }}>
        {/* Top bar */}
        <Box sx={{ pt: 2.5, pb: 1.5, display: "flex", alignItems: "center", gap: 1.5 }}>
          <IconButton onClick={() => navigate("/lms/student/quiz/play")} size="small" sx={{ color: "rgba(255,255,255,0.8)", bgcolor: "rgba(255,255,255,0.08)", borderRadius: "10px", "&:hover": { bgcolor: "rgba(255,255,255,0.14)" } }}>
            <ArrowBack fontSize="small" />
          </IconButton>
          <Box sx={{ width: 36, height: 36, borderRadius: "12px", background: "linear-gradient(135deg, #1565C0, #42A5F5)", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 0 14px rgba(66,165,245,0.4)" }}>
            <Bolt sx={{ fontSize: 19, color: "#fff" }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 900, fontSize: "0.58rem", color: "rgba(255,255,255,0.4)", letterSpacing: 3, textTransform: "uppercase" }}>Practice Mode</Typography>
            <Typography sx={{ fontWeight: 800, fontSize: "0.96rem", color: "#fff", lineHeight: 1.2 }}>AI Practice Quiz</Typography>
          </Box>
        </Box>
        <Box sx={{ height: "1px", bgcolor: "rgba(255,255,255,0.07)", mb: 3 }} />

        {/* Heading */}
        <Box sx={{ textAlign: "center", mb: 3, animation: `${popIn} 0.35s ease` }}>
          <Typography sx={{ fontWeight: 900, fontSize: "1.3rem", color: "#fff", mb: 0.5 }}>
            What would you like to practice? 🎯
          </Typography>
          <Typography sx={{ fontSize: "0.85rem", color: "rgba(255,255,255,0.4)" }}>
            Choose a subject — we'll build a personalised quiz just for you
          </Typography>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2, borderRadius: "12px" }}>{error}</Alert>}

        {/* Subject grid */}
        {subjectsLoading ? (
          <Grid container spacing={1.5} sx={{ mb: 3 }}>
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <Grid item xs={6} sm={4} key={i}>
                <Skeleton variant="rounded" height={110} sx={{ borderRadius: "20px", bgcolor: "rgba(255,255,255,0.07)" }} />
              </Grid>
            ))}
          </Grid>
        ) : (
          <Grid container spacing={1.5} sx={{ mb: 3 }}>
            {subjects.map((name, idx) => (
              <Grid item xs={6} sm={4} key={name}>
                <SubjectCard
                  name={name}
                  palette={SUBJECT_PALETTES[idx % SUBJECT_PALETTES.length]}
                  onClick={() => handleSubjectSelect(name)}
                  delay={`${idx * 0.05}s`}
                  isGenerating={isGenerating}
                  personalLevel={subjectLevelsMap[name.toLowerCase()]?.level}
                />
              </Grid>
            ))}
          </Grid>
        )}

        {/* "Other subject" custom input */}
        {!showCustomInput ? (
          <Box sx={{ textAlign: "center", mb: 4 }}>
            <Button
              onClick={() => setShowCustomInput(true)}
              startIcon={<Add />}
              sx={{ color: "rgba(255,255,255,0.45)", fontWeight: 700, fontSize: "0.82rem", "&:hover": { color: "rgba(255,255,255,0.75)", bgcolor: "rgba(255,255,255,0.06)" }, borderRadius: "10px" }}
            >
              Other subject
            </Button>
          </Box>
        ) : (
          <Box sx={{ maxWidth: 400, mx: "auto", mb: 4, animation: `${popIn} 0.3s ease` }}>
            <Box sx={{ display: "flex", gap: 1, alignItems: "center", background: "rgba(255,255,255,0.06)", border: "1px solid rgba(66,165,245,0.4)", borderRadius: "14px", px: 2, py: 0.5, "&:focus-within": { borderColor: "rgba(66,165,245,0.7)" } }}>
              <TextField
                fullWidth autoFocus variant="standard"
                value={customSubject}
                onChange={(e) => setCustomSubject(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && customSubject.trim()) handleSubjectSelect(customSubject.trim()); }}
                placeholder="Type any subject…"
                InputProps={{ disableUnderline: true }}
                sx={{ "& .MuiInputBase-input": { color: "#fff", fontSize: "0.95rem", py: 1, "&::placeholder": { color: "rgba(255,255,255,0.25)", opacity: 1 } } }}
              />
              <Button
                onClick={() => { if (customSubject.trim()) handleSubjectSelect(customSubject.trim()); }}
                disabled={!customSubject.trim()}
                variant="contained"
                sx={{ background: customSubject.trim() ? "linear-gradient(90deg, #1565C0, #42A5F5)" : "rgba(255,255,255,0.07)", color: customSubject.trim() ? "#fff" : "rgba(255,255,255,0.3)", fontWeight: 800, borderRadius: "10px", px: 2, flexShrink: 0, "&.Mui-disabled": { background: "rgba(255,255,255,0.07)", color: "rgba(255,255,255,0.3)" }, boxShadow: "none" }}
              >
                Next
              </Button>
            </Box>
          </Box>
        )}

        {/* Topic Picker Dialog */}
        <TopicPicker
          open={Boolean(topicPickerSubject)}
          onClose={() => setTopicPickerSubject(null)}
          subject={topicPickerSubject || ""}
          accentColor="#42A5F5"
          lastSessionTopics={lastTopicsForSubject}
          onStart={handleTopicChosen}
        />
      </Box>
    );
  }

  if (step === STEP_QUIZ && quiz) {
    return (
      <PracticeBg>
        <QuizPlayer
          quiz={quiz}
          onDone={handleQuizDoneWithLevel}
          onBack={() => { if (userId) clearActiveQuizSession(userId); setStep(STEP_CHAT); }}
          accentColor="#42A5F5"
          mode="PRACTICE"
        />
      </PracticeBg>
    );
  }

  if (step === STEP_RESULT && completedQuizId) {
    if (showReview) {
      return (
        <ReviewStep
          quizId={completedQuizId}
          onBack={() => setShowReview(false)}
          onDone={handleNewQuiz}
        />
      );
    }
    return (
      <PracticeBg>
        <LevelUpModal
          open={Boolean(levelUpData)}
          onClose={() => setLevelUpData(null)}
          subject={levelUpData?.subject || ""}
          newLevel={levelUpData?.level || ""}
          xpEarned={levelUpData?.xp_earned || 0}
        />
        <ResultStep quizId={completedQuizId} onReview={handleReview} onNewQuiz={handleNewQuiz} />
      </PracticeBg>
    );
  }

  return null;
};

export default PracticeQuizFlow;
